import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { CATEGORY_META, FILTER_TAGS, tx } from "@/lib/i18n";
import { PostCard } from "@/components/PostCard";
import { ChevronRight, SlidersHorizontal } from "lucide-react";

export default function CategoryList() {
  const location = useLocation();
  const slug = location.pathname.replace(/^\//, "").split("/")[0];
  const { lang } = useLang();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const meta = CATEGORY_META[slug];

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams({ category: slug, page: String(page), limit: "12" });
    if (filter) qs.set("filter", filter);
    api.get(`/posts?${qs}`).then((r) => {
      setItems(r.data.items);
      setTotal(r.data.total);
      setLoading(false);
    });
  }, [slug, page, filter]);

  if (!meta) return <div className="p-10">Not found</div>;

  return (
    <div>
      <div className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <nav className="flex items-center gap-1.5 text-xs text-slate-400 mb-4" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-white transition-colors">{tx("nav.home", lang)}</Link>
            <ChevronRight size={12} />
            <span className="text-white">{meta[lang === "hi" ? "hi" : "en"]}</span>
          </nav>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            {meta[lang === "hi" ? "hi" : "en"]}
          </h1>
          <p className="mt-2 text-sm text-slate-400 font-mono-tabular">
            {total} {lang === "hi" ? "अधिसूचनाएँ" : "notifications"}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid gap-8 lg:grid-cols-4">
        <aside className="lg:col-span-1">
          <div className="sticky top-32">
            <div className="flex items-center gap-2 mb-3 text-xs font-bold tracking-[0.14em] uppercase text-slate-500">
              <SlidersHorizontal size={13} /> {tx("ui.filter_by", lang)}
            </div>
            <div className="flex flex-wrap gap-1.5 lg:flex-col lg:gap-1">
              <button
                data-testid="filter-all"
                onClick={() => { setFilter(""); setPage(1); }}
                className={`text-left text-xs px-2.5 py-1.5 rounded-sm border transition-colors ${
                  filter === "" ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"
                }`}
              >
                {tx("ui.all", lang)}
              </button>
              {FILTER_TAGS.map((t) => (
                <button
                  key={t.slug}
                  data-testid={`filter-${t.slug}`}
                  onClick={() => { setFilter(t.slug); setPage(1); }}
                  className={`text-left text-xs px-2.5 py-1.5 rounded-sm border transition-colors ${
                    filter === t.slug ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"
                  }`}
                >
                  {t[lang === "hi" ? "hi" : "en"]}
                </button>
              ))}
            </div>
          </div>
        </aside>

        <div className="lg:col-span-3">
          {loading ? (
            <div className="grid gap-4 grid-cols-1 sm:grid-cols-2">
              {[0,1,2,3].map(i => <div key={i} className="h-40 bg-white border border-slate-200 rounded-md animate-pulse" />)}
            </div>
          ) : items.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-md p-10 text-center text-slate-500" data-testid="empty-state">
              {tx("ui.no_results", lang)}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {items.map((p) => <PostCard key={p.id} post={p} />)}
              </div>
              {total > 12 && (
                <div className="mt-6 flex items-center justify-center gap-2">
                  <button
                    data-testid="page-prev"
                    disabled={page === 1}
                    onClick={() => setPage((p) => p - 1)}
                    className="px-3 py-1.5 text-sm border border-slate-300 rounded-sm disabled:opacity-40 hover:bg-slate-100"
                  >Prev</button>
                  <span className="text-xs text-slate-500 font-mono-tabular">
                    Page {page} / {Math.ceil(total / 12)}
                  </span>
                  <button
                    data-testid="page-next"
                    disabled={page * 12 >= total}
                    onClick={() => setPage((p) => p + 1)}
                    className="px-3 py-1.5 text-sm border border-slate-300 rounded-sm disabled:opacity-40 hover:bg-slate-100"
                  >Next</button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
